import React, { useCallback, useEffect, useState } from "react";
import { DashboardLayout } from "./DashboardLayout";
import { api, MXN, formatDateMX } from "../../lib/apiClient";
import { QUOTE_STATUSES } from "../../lib/constants";
import { adminQuoteWhatsappMessage, openWhatsappBusiness } from "../../lib/whatsapp";
import { toast } from "sonner";
import { X, MessageCircle, Save } from "lucide-react";
import { useAdminRealtime } from "../../hooks/useAdminRealtime";

export default function Servicios() {
  const [list, setList] = useState([]);
  const [settings, setSettings] = useState({});
  const [sel, setSel] = useState(null);
  const [filter, setFilter] = useState("");

  const load = useCallback(() => api.get("/event-requests", { params: filter ? { status: filter } : {} }).then((r) => setList(r.data)).catch(() => {}), [filter]);
  useEffect(() => { load(); }, [load]);
  useAdminRealtime(load, ["events"]);
  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const save = async () => {
    await api.patch(`/event-requests/${sel.id}`, { status: sel.status, quoted_price: parseFloat(sel.quoted_price) || 0, internal_notes: sel.internal_notes });
    toast.success("Solicitud actualizada"); setSel(null); load();
  };
  const contactCustomer = () => {
    if (!sel?.customer_phone) {
      toast.error("Esta solicitud no tiene WhatsApp del cliente.");
      return;
    }
    openWhatsappBusiness(sel.customer_phone, adminQuoteWhatsappMessage(sel));
  };

  return (
    <DashboardLayout title="Servicios y cotizaciones">
      <select data-testid="serv-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="w-full sm:w-auto bg-blancoCalido border-2 border-beige rounded-2xl sm:rounded-full px-4 py-3 sm:py-2.5 text-sm mb-5 focus:outline-none focus:border-vino">
        <option value="">Todas</option>
        {QUOTE_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
      </select>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((r) => (
          <button key={r.id} onClick={() => setSel(r)} data-testid={`serv-${r.id}`} className="w-full min-w-0 text-left bg-blancoCalido border border-beige rounded-[22px] p-5 shadow-pastel hover:-translate-y-1 transition-all">
            <div className="flex justify-between gap-2 min-w-0"><p className="font-medium text-vino truncate">{r.folio}</p><span className="shrink-0 text-xs px-2.5 py-1 rounded-full bg-beige text-vino">{QUOTE_STATUSES.find((s) => s.value === r.status)?.label}</span></div>
            <p className="text-sm text-cafe mt-1">{r.customer_name} · {r.event_type || "Evento"}</p>
            <p className="text-xs text-cafe/50 mt-0.5">{formatDateMX(r.event_date)} · {r.guests} personas</p>
            <p className="text-xs text-cafe/60 mt-2">{r.services?.join(", ")}</p>
          </button>
        ))}
      </div>
      {list.length === 0 && <p className="text-cafe/50 text-sm">Sin solicitudes.</p>}

      {sel && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="serv-drawer">
          <div className="absolute inset-0 bg-vinoDark/30 backdrop-blur-sm" onClick={() => setSel(null)} />
          <div className="relative w-full max-w-md bg-crema h-full overflow-y-auto overflow-x-hidden p-4 sm:p-6 pb-[calc(6rem+env(safe-area-inset-bottom))] sm:pb-6">
            <div className="flex justify-between items-center gap-3 mb-4"><div className="min-w-0"><p className="text-xs text-cafe/50">Folio</p><p className="font-serif text-2xl text-vino truncate">{sel.folio}</p></div><button onClick={() => setSel(null)} className="text-vino p-2 shrink-0"><X /></button></div>
            <div className="bg-blancoCalido border border-beige rounded-[20px] p-4 space-y-1 text-sm mb-3">
              <Info l="Cliente" v={sel.customer_name} /><Info l="Teléfono" v={sel.customer_phone} />
              <Info l="Tipo" v={sel.event_type} /><Info l="Fecha" v={formatDateMX(sel.event_date)} />
              <Info l="Lugar" v={sel.location} /><Info l="Personas" v={sel.guests} />
              <Info l="Servicios" v={(sel.services || []).join(", ")} />
              {sel.comments && <Info l="Comentarios" v={sel.comments} />}
            </div>
            <div className="bg-blancoCalido border border-beige rounded-[20px] p-4 space-y-3">
              <div><label className="text-xs text-cafe/60">Estado de cotización</label>
                <select data-testid="serv-status" value={sel.status} onChange={(e) => setSel({ ...sel, status: e.target.value })} className="w-full bg-crema border-2 border-beige rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-vino">
                  {QUOTE_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select></div>
              <div><label className="text-xs text-cafe/60">Precio cotizado (MXN)</label>
                <input data-testid="serv-price" type="number" value={sel.quoted_price || ""} onChange={(e) => setSel({ ...sel, quoted_price: e.target.value })} className="w-full bg-crema border-2 border-beige rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-vino" /></div>
              <div><label className="text-xs text-cafe/60">Notas internas</label>
                <textarea value={sel.internal_notes || ""} onChange={(e) => setSel({ ...sel, internal_notes: e.target.value })} rows={2} className="w-full bg-crema border-2 border-beige rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-vino" /></div>
              <button onClick={save} data-testid="serv-save" className="w-full justify-center bg-vino text-crema rounded-full px-5 py-2.5 text-sm flex items-center gap-2"><Save className="w-4 h-4" /> Guardar</button>
            </div>
            <button type="button" onClick={contactCustomer} className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#25D366] text-white rounded-full py-3 font-medium"><MessageCircle className="w-5 h-5" /> WhatsApp Business</button>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
const Info = ({ l, v }) => <div className="flex flex-col min-[380px]:flex-row min-[380px]:justify-between gap-0.5 min-[380px]:gap-3 min-w-0"><span className="text-cafe/60 shrink-0">{l}</span><span className="text-cafe min-[380px]:text-right break-words min-w-0">{v}</span></div>;
